import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import DashboardService from 'api/dashboard/DashboardService';
import { TopMaintenanceComponentDashboardDto } from 'api/dashboard/dtos/TopMaintenanceComponentDashboardDto';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useInfiniteReveal from '../components/useInfiniteReveal';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import openMaintenancesList from '../utils/openMaintenancesList';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './TopMaintenanceComponents.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];


type Props = {
	data?: TopMaintenanceComponentDashboardDto[] | null;
	isLoading?: boolean;
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const TopMaintenanceComponents = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<TopMaintenanceComponentDashboardDto[] | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('topMaintenanceComponents');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	const isLegacyMode = props.data !== undefined;

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getTopMaintenanceComponents(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const prevCriteria = props.filters
					? { ...props.filters, dateFrom: range.prevFrom, dateTo: range.prevTo }
					: { dateFrom: range.prevFrom, dateTo: range.prevTo, ...vehicleScope };
				const previous = await DashboardService.getTopMaintenanceComponents(prevCriteria);
				if (!cancelled) {
					setPreviousTotal((previous || []).reduce((s, x) => s + x.totalCount, 0));
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const renderOptions = !isLegacyMode
		? <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />
		: undefined;

	if (!data || data.length === 0) {
		return (
			<Box
				className={tableStyles.container}
				title={t('dashboard.top_maintenance_components' as any) as string}
				subTitleTooltip={t('dashboard.top_maintenance_components_tooltip' as any) as string}
				renderOptions={renderOptions}
			>
				{isLoading ? (
					<div className={styles.centerAlign}>
						<LoadingSpinner />
					</div>
				) : (
					<WidgetNoData />
				)}
			</Box>
		);
	}

	return (
		<TopMaintenanceComponentsList
			data={data}
			isLoading={!!isLoading}
			renderOptions={renderOptions}
			previousTotal={previousTotal}
			previousRange={previousRange}
			dateFrom={dateFrom}
			dateTo={dateTo}
		/>
	);
};

type ListProps = {
	data: TopMaintenanceComponentDashboardDto[];
	isLoading: boolean;
	renderOptions?: React.ReactElement;
	previousTotal: number | null;
	previousRange: PreviousRange | null;
	dateFrom: Date;
	dateTo: Date;
};

const TopMaintenanceComponentsList = ({ data, isLoading, renderOptions, previousTotal, previousRange, dateFrom, dateTo }: ListProps) => {
	const { t } = useTranslation();
	const maxCount = data[0].totalCount;
	const grandTotal = data.reduce((sum, it) => sum + it.totalCount, 0);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.top_maintenance_components' as any) as string}
			subTitleTooltip={t('dashboard.top_maintenance_components_tooltip' as any) as string}
			renderOptions={renderOptions}
		>
			{isLoading ? (
				<div className={styles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : (
				<>
					<KpiHeader
						value={grandTotal.toLocaleString('fr-FR')}
						subLabel={t('dashboard.total_interventions' as any) as string}
						currentTotal={grandTotal}
						previousTotal={previousTotal}
						previousRange={previousRange}
						onClick={() => openMaintenancesList({ dateFrom, dateTo })}
					/>
					<div>
					{visibleItems.map((item, index) => {
						const percentage = maxCount > 0 ? (item.totalCount / maxCount) * 100 : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.componentId}
								className={`${styles.item} ${styles.itemClickable}`}
								onClick={() => openMaintenancesList({
									dateFrom,
									dateTo,
									componentIds: [item.componentId],
									componentNames: [item.componentName],
								})}
							>
								<div className={styles.itemHeader}>
									<span className={styles.itemName}>{item.componentName}</span>
									<span className={styles.itemCount}>{item.totalCount}{t('dashboard.times_suffix' as any) as string}</span>
								</div>
								<div className={styles.progressTrack}>
									<div
										className={styles.progressFill}
										style={{ width: `${percentage}%`, background: color }}
									/>
								</div>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
				</>
			)}
		</Box>
	);
};

export default TopMaintenanceComponents;
